import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const SWIPE_THRESHOLD = 50;

function ImageLightbox({ images, index, onIndexChange, onClose }) {
  const touchStartX = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const count = images.length;
  const hasMultiple = count > 1;

  const showPrevious = () => onIndexChange((index - 1 + count) % count);
  const showNext = () => onIndexChange((index + 1) % count);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasMultiple) onIndexChange((index - 1 + count) % count);
      else if (e.key === 'ArrowRight' && hasMultiple) onIndexChange((index + 1) % count);
      else if (e.key === 'Tab') {
        const buttons = dialogRef.current.querySelectorAll('button');
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        const focusInside = dialogRef.current.contains(document.activeElement);
        if (!focusInside || (e.shiftKey && document.activeElement === first)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [index, count, hasMultiple, onIndexChange, onClose]);

  useEffect(() => {
    const root = document.documentElement;
    const previousStyle = { overflow: root.style.overflow, scrollbarGutter: root.style.scrollbarGutter };
    const previousFocus = document.activeElement;
    root.style.overflow = 'hidden';
    root.style.scrollbarGutter = 'stable';
    closeButtonRef.current.focus();
    return () => {
      Object.assign(root.style, previousStyle);
      previousFocus?.focus?.();
    };
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || !hasMultiple) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (deltaX > SWIPE_THRESHOLD) showPrevious();
    else if (deltaX < -SWIPE_THRESHOLD) showNext();
  };

  const stop = (handler) => (e) => {
    e.stopPropagation();
    handler();
  };

  const navButtonClass = 'absolute top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors';

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={stop(onClose)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={images[index].url}
        alt={`${index + 1} of ${count}`}
        className="max-w-full max-h-full object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
      <button ref={closeButtonRef} type="button" aria-label="Close photo viewer" onClick={stop(onClose)} className="absolute top-4 right-4 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
        <X className="w-6 h-6" />
      </button>
      {hasMultiple && (
        <>
          <button type="button" aria-label="Previous photo" onClick={stop(showPrevious)} className={`${navButtonClass} left-4`}>
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button type="button" aria-label="Next photo" onClick={stop(showNext)} className={`${navButtonClass} right-4`}>
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs">
            {index + 1} / {count}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}

export default ImageLightbox;
